// Site-wide motion helpers, registered once in main.js.
//   v-reveal            fade/slide an element in the first time it scrolls into view
//   v-reveal="{ delay }" same, with a stagger delay in ms
//   v-count="{ value, prefix, suffix }"  count a number up when it scrolls into view
//                        (element needs a child with class "num-val")
//   v-spotlight          soft gold glow that follows the mouse over a card
// Everything turns itself off for people whose device asks for reduced motion.
import Vue from 'vue'

export const reduceMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

let revealObserver = null
function getRevealObserver() {
  if (revealObserver || typeof IntersectionObserver === 'undefined') return revealObserver
  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return
      const el = e.target
      el.classList.add('is-in')
      revealObserver.unobserve(el)
      // Drop the stagger delay once it has played so hover effects stay snappy.
      if (el.style.transitionDelay) setTimeout(() => { el.style.transitionDelay = '' }, 1400)
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
  return revealObserver
}

Vue.directive('reveal', {
  inserted(el, binding) {
    el.classList.add('rv')
    const v = binding.value || {}
    if (v.delay) el.style.transitionDelay = v.delay + 'ms'
    if (binding.modifiers.left) el.classList.add('rv-left')
    if (binding.modifiers.right) el.classList.add('rv-right')
    if (binding.modifiers.zoom) el.classList.add('rv-zoom')
    const obs = getRevealObserver()
    if (!obs || reduceMotion()) { el.classList.add('is-in'); return }
    obs.observe(el)
  },
  unbind(el) { if (revealObserver) revealObserver.unobserve(el) },
})

Vue.directive('count', {
  inserted(el, binding) {
    const n = binding.value || {}
    const out = el.querySelector('.num-val') || el
    const fmt = (v) => (n.prefix || '') + Number(v).toLocaleString('en-US') + (n.suffix || '')
    if (reduceMotion() || typeof IntersectionObserver === 'undefined') { out.textContent = fmt(n.value); return }
    out.textContent = fmt(0)
    const obs = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return
      obs.disconnect()
      const start = performance.now(), dur = 1400
      const step = (t) => {
        const p = Math.min(1, (t - start) / dur)
        out.textContent = fmt(Math.round(n.value * (1 - Math.pow(1 - p, 3))))
        if (p < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }, { threshold: 0.4 })
    obs.observe(el)
  },
})

Vue.directive('spotlight', {
  inserted(el) {
    el.classList.add('sz-spot')
    el._spot = (e) => {
      const r = el.getBoundingClientRect()
      el.style.setProperty('--mx', (e.clientX - r.left) + 'px')
      el.style.setProperty('--my', (e.clientY - r.top) + 'px')
    }
    el.addEventListener('mousemove', el._spot)
  },
  unbind(el) { if (el._spot) el.removeEventListener('mousemove', el._spot) },
})
